import * as React from "react";
import { Link } from "@tanstack/react-router";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Button,
  Badge,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  ScrollArea,
} from "@repo/ui";
import {
  Bell,
  CheckCheck,
  ArrowRight,
  CheckCircle2,
  Clock,
  ExternalLink,
  UserCircle,
} from "lucide-react";
import { AlertCard } from "./alert-card";
import { EntityReferenceBadge } from "@/components/reminders/entity-reference-badge";
import type { Alert, AlertCounts } from "@/lib/types/alerts";
import type { Reminder } from "@/hooks/use-reminders";
import {
  getPriorityColor,
  getPriorityLabel,
} from "@/lib/utils/reminder-format";
import { formatToApiDate } from "@repo/utils";

export interface NotificationCenterProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  alerts: Alert[];
  counts: AlertCounts;
  reminders: Reminder[];
  onDismiss: (alertKey: string) => void;
  onDismissAll: () => void;
  onRemind?: (alert: Alert) => void;
  onCompleteReminder?: (id: string) => void;
}

function ReminderRow({
  reminder,
  onComplete,
  onClose,
}: {
  reminder: Reminder;
  onComplete?: (id: string) => void;
  onClose?: () => void;
}) {
  const today = formatToApiDate(new Date());
  const isOverdue = reminder.due_date < today;
  const isDueToday = reminder.due_date === today;

  return (
    <div className="rounded-xl border border-border/60 bg-surface px-4 py-3 transition-colors hover:bg-muted/20">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold truncate">
              {reminder.title}
            </span>
            <span className="flex items-center gap-1.5 shrink-0">
              <span
                className={`inline-block h-1.5 w-1.5 rounded-full ${getPriorityColor(reminder.priority)}`}
              />
              <span className="text-[11px] text-muted-foreground">
                {getPriorityLabel(reminder.priority)}
              </span>
            </span>
          </div>

          {reminder.description && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
              {reminder.description}
            </p>
          )}

          {/* Patient name and linked entity */}
          {(reminder.patient_name ||
            (reminder.reference_type && reminder.reference_id)) && (
            <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
              {reminder.patient_name && (
                <Badge variant="outline" className="gap-1 text-xs h-5 px-1.5">
                  <UserCircle className="h-3 w-3 text-secondary" />
                  <span className="truncate max-w-[160px]">
                    {reminder.patient_name}
                  </span>
                </Badge>
              )}
              {reminder.reference_type && reminder.reference_id && (
                <EntityReferenceBadge
                  referenceType={reminder.reference_type}
                  referenceId={reminder.reference_id}
                />
              )}
            </div>
          )}

          <div className="flex items-center gap-1.5 mt-2">
            <Clock className="h-3 w-3 text-muted-foreground shrink-0" />
            <span
              className={`text-xs ${
                isOverdue
                  ? "text-destructive font-medium"
                  : isDueToday
                    ? "text-warning font-medium"
                    : "text-muted-foreground"
              }`}
            >
              {isOverdue
                ? "Overdue"
                : isDueToday
                  ? "Due today"
                  : new Date(
                      reminder.due_date + "T00:00:00"
                    ).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
              {reminder.due_time && ` · ${reminder.due_time.slice(0, 5)}`}
            </span>
            <div className="ml-auto flex items-center gap-1">
              {onComplete && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onComplete(reminder.id)}
                  className="h-7 text-xs px-2 text-muted-foreground hover:text-foreground"
                  title="Mark as done"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <span className="ml-1">Complete</span>
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-1.5 text-muted-foreground hover:text-foreground"
                title="Open in Reminders"
                asChild
                onClick={onClose}
              >
                <Link to="/reminders" search={{ id: reminder.id }}>
                  <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function NotificationCenter({
  open,
  onOpenChange,
  alerts,
  counts,
  reminders,
  onDismiss,
  onDismissAll,
  onRemind,
  onCompleteReminder,
}: NotificationCenterProps) {
  const [activeTab, setActiveTab] = React.useState<"alerts" | "reminders">(
    "alerts"
  );

  const activeReminders = React.useMemo(
    () => reminders.filter((r) => !r.is_completed),
    [reminders]
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-muted-foreground" />
              <SheetTitle>Notifications</SheetTitle>
            </div>
            {activeTab === "alerts" && alerts.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onDismissAll}
                className="gap-2"
              >
                <CheckCheck className="h-4 w-4" />
                Dismiss All
              </Button>
            )}
          </div>
          <SheetDescription>Alerts and reminders</SheetDescription>
        </SheetHeader>

        <div className="mt-6">
          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as "alerts" | "reminders")}
          >
            <TabsList className="w-full">
              <TabsTrigger value="alerts" className="flex-1">
                Alerts
                {counts.total > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-1.5 h-4 px-1.5 text-[10px]"
                  >
                    {counts.total}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="reminders" className="flex-1">
                Reminders
                {activeReminders.length > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-1.5 h-4 px-1.5 text-[10px]"
                  >
                    {activeReminders.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Alerts tab */}
            <TabsContent value="alerts" className="mt-4">
              <ScrollArea className="h-[calc(100vh-300px)]">
                <div className="space-y-2 pr-4">
                  {alerts.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="mx-auto h-10 w-10 rounded-lg bg-muted/60 flex items-center justify-center mb-3">
                        <Bell className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <p className="text-sm font-medium">No alerts</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Everything looks good
                      </p>
                    </div>
                  ) : (
                    alerts.map((alert) => (
                      <AlertCard
                        key={alert.id}
                        {...alert}
                        onDismiss={onDismiss}
                        onRemind={onRemind ? () => onRemind(alert) : undefined}
                      />
                    ))
                  )}
                </div>
              </ScrollArea>

              {alerts.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <Button
                    variant="ghost"
                    className="w-full"
                    asChild
                    onClick={() => onOpenChange(false)}
                  >
                    <Link to="/alerts">
                      View All Alerts
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* Reminders tab */}
            <TabsContent value="reminders" className="mt-4">
              <ScrollArea className="h-[calc(100vh-300px)]">
                <div className="space-y-2 pr-4">
                  {activeReminders.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="mx-auto h-10 w-10 rounded-lg bg-muted/60 flex items-center justify-center mb-3">
                        <CheckCircle2 className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <p className="text-sm font-medium">No active reminders</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        All caught up
                      </p>
                    </div>
                  ) : (
                    activeReminders.map((reminder) => (
                      <ReminderRow
                        key={reminder.id}
                        reminder={reminder}
                        onComplete={onCompleteReminder}
                        onClose={() => onOpenChange(false)}
                      />
                    ))
                  )}
                </div>
              </ScrollArea>

              <div className="mt-4 pt-4 border-t">
                <Button
                  variant="ghost"
                  className="w-full"
                  asChild
                  onClick={() => onOpenChange(false)}
                >
                  <Link to="/reminders">
                    View All Reminders
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
}
